import { useEffect } from 'react';

/**
 * useRouteMeta — route-scoped SEO / Open Graph / Twitter card metadata
 * injection for the HussarAi SPA.
 *
 * Centralises what `/ai-act-polska` and `/operator-zgodnosci` were each
 * doing inline. Same DOM output, half the surface area.
 *
 * Behaviour contract (preserved from the previous inline implementations):
 *   • Every injected <meta>/<link> node is tagged with
 *     `data-hussar-meta="{marker}"` so the unmount cleanup removes ONLY
 *     this route's nodes — never the global ones from public/index.html
 *     or another page's metadata.
 *   • Re-runs whenever any provided value changes, so a PL↔EN language
 *     toggle correctly re-injects the new strings.
 *   • Safe for SSR / first paint — guards `window`/`document`.
 *
 * Pass `null`/`undefined` for any field you don't want to set.
 *
 * @param {object} cfg
 * @param {string} cfg.marker            REQUIRED. Stable kebab-case route key.
 * @param {string} cfg.title             <title> + og:title default
 * @param {string} [cfg.description]     <meta name=description> + og:description default
 * @param {string} [cfg.canonical]       <link rel=canonical href=...>
 * @param {string} [cfg.locale]          og:locale (e.g. 'pl_PL', 'en_GB')
 * @param {string} [cfg.ogTitle]         override og:title
 * @param {string} [cfg.ogDescription]   override og:description
 * @param {string} [cfg.ogImage]
 * @param {string} [cfg.ogUrl]
 * @param {string} [cfg.ogType]          default 'website'
 * @param {string} [cfg.ogSiteName]      default 'HussarAi'
 * @param {string|number} [cfg.ogImageWidth]
 * @param {string|number} [cfg.ogImageHeight]
 * @param {string} [cfg.ogImageAlt]
 * @param {string} [cfg.twitterCard]     default 'summary_large_image'
 * @param {string} [cfg.twitterTitle]
 * @param {string} [cfg.twitterDescription]
 * @param {string} [cfg.twitterImage]
 * @param {string} [cfg.twitterImageAlt]
 */
export default function useRouteMeta(cfg) {
  const {
    marker,
    title,
    description,
    canonical,
    locale,
    ogTitle,
    ogDescription,
    ogImage,
    ogUrl,
    ogType,
    ogSiteName,
    ogImageWidth,
    ogImageHeight,
    ogImageAlt,
    twitterCard,
    twitterTitle,
    twitterDescription,
    twitterImage,
    twitterImageAlt,
  } = cfg || {};

  useEffect(() => {
    if (typeof document === 'undefined' || !marker) return undefined;

    // Snapshot what we change, so cleanup can RESTORE pre-existing tags
    // (e.g. the static OG fallback in public/index.html) and REMOVE tags
    // we created ourselves.
    const originalTitle = document.title;
    const restorations = []; // [{ el, attr, prior }]
    const created = [];      // [el] — nodes we created, safe to remove on unmount

    if (title) document.title = title;

    /** Create-or-update a <meta> tag.
     *  • If a tag matching `selector` already exists, store its prior content
     *    and update in place — we'll restore on cleanup.
     *  • If it doesn't, create one tagged with the route marker for cleanup.
     */
    const setMeta = (selector, attrs, content) => {
      if (content === undefined || content === null || content === '') return;
      let el = document.head.querySelector(selector);
      if (el) {
        restorations.push({ el, attr: 'content', prior: el.getAttribute('content') });
        el.setAttribute('content', String(content));
      } else {
        el = document.createElement('meta');
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
        el.setAttribute('data-hussar-meta', marker);
        el.setAttribute('content', String(content));
        document.head.appendChild(el);
        created.push(el);
      }
    };

    // --- Standard ---
    setMeta('meta[name="description"]', { name: 'description' }, description);

    // --- Open Graph ---
    const effectiveOgTitle       = ogTitle       ?? title;
    const effectiveOgDescription = ogDescription ?? description;
    setMeta('meta[property="og:title"]',        { property: 'og:title' },        effectiveOgTitle);
    setMeta('meta[property="og:description"]',  { property: 'og:description' },  effectiveOgDescription);
    setMeta('meta[property="og:image"]',        { property: 'og:image' },        ogImage);
    setMeta('meta[property="og:image:width"]',  { property: 'og:image:width' },  ogImageWidth);
    setMeta('meta[property="og:image:height"]', { property: 'og:image:height' }, ogImageHeight);
    setMeta('meta[property="og:image:alt"]',    { property: 'og:image:alt' },    ogImageAlt ?? effectiveOgTitle);
    setMeta('meta[property="og:url"]',          { property: 'og:url' },          ogUrl);
    setMeta('meta[property="og:type"]',         { property: 'og:type' },         ogType ?? 'website');
    setMeta('meta[property="og:site_name"]',    { property: 'og:site_name' },    ogSiteName ?? 'HussarAi');
    setMeta('meta[property="og:locale"]',       { property: 'og:locale' },       locale);

    // --- Twitter / X ---
    setMeta('meta[name="twitter:card"]',        { name: 'twitter:card' },        twitterCard ?? 'summary_large_image');
    setMeta('meta[name="twitter:title"]',       { name: 'twitter:title' },       twitterTitle ?? effectiveOgTitle);
    setMeta('meta[name="twitter:description"]', { name: 'twitter:description' }, twitterDescription ?? effectiveOgDescription);
    setMeta('meta[name="twitter:image"]',       { name: 'twitter:image' },       twitterImage ?? ogImage);
    setMeta('meta[name="twitter:image:alt"]',   { name: 'twitter:image:alt' },   twitterImageAlt ?? ogImageAlt ?? effectiveOgTitle);

    // --- Canonical link ---
    if (canonical) {
      // Update an existing top-level canonical (e.g. from index.html) AND
      // restore it on cleanup, instead of accumulating duplicates.
      let link = document.head.querySelector('link[rel="canonical"]');
      if (link) {
        restorations.push({ el: link, attr: 'href', prior: link.getAttribute('href') });
        link.setAttribute('href', canonical);
      } else {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        link.setAttribute('href', canonical);
        link.setAttribute('data-hussar-meta', marker);
        document.head.appendChild(link);
        created.push(link);
      }
    }

    return () => {
      // 1. Restore pre-existing tags (static fallback from index.html stays intact).
      restorations.forEach(({ el, attr, prior }) => {
        if (prior === null) {
          el.removeAttribute(attr);
        } else {
          el.setAttribute(attr, prior);
        }
      });
      // 2. Remove only nodes we created.
      created.forEach((el) => el.parentNode && el.parentNode.removeChild(el));
      // 3. Restore the document title.
      document.title = originalTitle;
    };
  }, [
    marker,
    title,
    description,
    canonical,
    locale,
    ogTitle,
    ogDescription,
    ogImage,
    ogUrl,
    ogType,
    ogSiteName,
    ogImageWidth,
    ogImageHeight,
    ogImageAlt,
    twitterCard,
    twitterTitle,
    twitterDescription,
    twitterImage,
    twitterImageAlt,
  ]);
}
